// Capture store namespace (window.backlit.capture). Backed by the data
// API at POST/GET /_backlit/capture and GET/PUT/DELETE
// /_backlit/capture/{handle}.
//
// Writes are open to any session: create() takes a required `prefix` (same
// charset as data/userdata keys) prepended to a server-minted GUID, so
// the stored name — and returned handle — is "{prefix}-{guid}"; update()
// overwrites by that handle (the handle is the capability). Under the
// offline capture opt-in ("capture" in offline.enable({stores})), the
// offline decorator (offline-backend.ts) mints the GUID on the device as a
// random v4 UUID instead, so a create made offline can be queued and
// replayed under its final handle. Reads,
// enumeration, and deletion are **admin only**: get(), list(), and
// delete() throw unless the signed-in user is an admin. This keeps the
// store write-only for ordinary app code — even if the page JS is
// compromised it cannot read back what it captured — while giving an
// owner/admin a way to inspect and clean up submissions.

import { normalizeBody } from "./body.js";
import {
  getBackend,
  type BlobResult,
  type CaptureCreateResult,
  type CaptureListEntry,
  type ListOptions,
  type ListPage,
  type PutResult,
} from "./backend.js";
import { validateDataKey, validateDataPrefix, validateHandle } from "./validate.js";

export interface CaptureNamespace {
  create(prefix: string, body: BodyInit, contentType: string): Promise<CaptureCreateResult>;
  update(handle: string, body: BodyInit, contentType: string): Promise<PutResult>;
  /**
   * Read the value stored at a handle. **Admin only** — on a remote glow
   * a non-admin (or signed-out / Public) caller throws `BacklitError`
   * (`forbidden` / `unauthenticated`); an unknown handle throws
   * `unknown_handle`. Resolves to the same `{value, contentType, crc32c}`
   * shape as `data.get`.
   */
  get(handle: string): Promise<BlobResult>;
  /**
   * List the handles stored in this glow's capture store, each with its
   * uncompressed-body CRC32C. **Admin only** (same gating as `get`). The
   * optional `prefix` matches against the handle, so passing a create
   * prefix lists that group (e.g. `list("contact")` → every `contact-*`
   * handle). Returns handles only, never values. Returns one bounded page
   * plus an opaque `next` cursor (DATA-1); pass `opts.cursor`/`opts.limit`
   * to page (limit clamped server-side to [1,1000], default 500).
   */
  list(prefix?: string, opts?: ListOptions): Promise<ListPage<CaptureListEntry>>;
  /**
   * Remove the object stored at a handle. **Admin only** (same gating as
   * `get`). Throws `unknown_handle` if the handle was never created.
   */
  delete(handle: string): Promise<void>;
}

export function createCapture(): CaptureNamespace {
  return {
    create(prefix: string, body: BodyInit, contentType: string): Promise<CaptureCreateResult> {
      // The prefix is a required, non-empty label (it is prepended to the
      // server-minted GUID), so it is validated with the data-key charset —
      // a bad/empty value throws invalid_key.
      validateDataKey(prefix);
      const norm = normalizeBody(body, contentType);
      return getBackend().captureCreate(prefix, norm.body, norm.contentType);
    },

    update(handle: string, body: BodyInit, contentType: string): Promise<PutResult> {
      validateHandle(handle);
      const norm = normalizeBody(body, contentType);
      return getBackend().captureUpdate(handle, norm.body, norm.contentType);
    },

    get(handle: string): Promise<BlobResult> {
      validateHandle(handle);
      return getBackend().captureGet(handle);
    },

    list(prefix: string = "", opts?: ListOptions): Promise<ListPage<CaptureListEntry>> {
      validateDataPrefix(prefix);
      return getBackend().captureList(prefix, opts);
    },

    delete(handle: string): Promise<void> {
      validateHandle(handle);
      return getBackend().captureDelete(handle);
    },
  };
}
